import { useState } from "react";
import { Link } from "wouter";
import { Shield, X, Phone, ExternalLink } from "lucide-react";
import { Button } from "@/components/ui/button";

interface SafetyBannerProps {
  variant?: "full" | "compact";
  showOnPersonalConnection?: boolean;
}

export function SafetyBanner({ variant = "compact", showOnPersonalConnection = false }: SafetyBannerProps) {
  const [dismissed, setDismissed] = useState(false);

  if (dismissed) return null;

  if (variant === "compact") {
    return (
      <div className="bg-primary/5 border border-primary/20 rounded-lg p-3 relative">
        <Button
          variant="ghost"
          size="icon"
          className="absolute top-1 right-1 h-6 w-6"
          onClick={() => setDismissed(true)}
        >
          <X className="w-4 h-4" />
        </Button>
        <div className="flex items-start gap-3 pr-6">
          <Shield className="w-5 h-5 text-primary flex-shrink-0 mt-0.5" />
          <div>
            <p className="text-sm font-medium">Your Safety Matters</p>
            <p className="text-xs text-muted-foreground mt-1">
              Report suspicious activity. Meet in public places. Never share personal financial information.
              {showOnPersonalConnection && (
                <span className="block mt-1 text-primary">
                  Personal connection ads require wallet verification for added security.
                </span>
              )}
            </p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="bg-gradient-to-r from-primary/10 to-secondary/10 border border-primary/20 rounded-xl p-6">
      <div className="flex items-start gap-4">
        <div className="bg-primary/20 rounded-full p-3">
          <Shield className="w-6 h-6 text-primary" />
        </div>
        <div className="flex-1">
          <h3 className="font-semibold text-lg">Community Safety Guidelines</h3>
          <p className="text-muted-foreground mt-2">
            Listo is committed to creating a safe marketplace. We actively monitor for illegal activity, 
            including human trafficking and child exploitation. Help us keep this community safe.
          </p>
          
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mt-4">
            <div className="space-y-2">
              <h4 className="font-medium text-sm">Safety Tips</h4>
              <ul className="text-sm text-muted-foreground space-y-1">
                <li>• Always meet in public, well-lit places</li>
                <li>• Tell someone where you're going</li>
                <li>• Never share banking or SSN information</li>
                <li>• Trust your instincts - if something feels wrong, leave</li>
              </ul>
            </div>
            
            <div className="space-y-2">
              <h4 className="font-medium text-sm">Report Concerns</h4>
              <ul className="text-sm text-muted-foreground space-y-1">
                <li>• Use the "Report" button on any listing</li>
                <li>• Email: safety@listo.app</li>
                <li className="flex items-center gap-1">
                  <Phone className="w-3 h-3" />
                  Human Trafficking Hotline: 1-888-373-7888
                </li>
                <li className="flex items-center gap-1">
                  <Phone className="w-3 h-3" />
                  NCMEC CyberTipline: 1-800-843-5678
                </li>
              </ul>
            </div>
          </div>

          <div className="flex flex-wrap gap-3 mt-4">
            <Button variant="outline" size="sm" asChild>
              <a 
                href="https://www.missingkids.org/gethelpnow/cybertipline" 
                target="_blank" 
                rel="noopener noreferrer"
                className="flex items-center gap-2"
              >
                <ExternalLink className="w-4 h-4" />
                Report to NCMEC
              </a>
            </Button>
            <Button variant="outline" size="sm" asChild>
              <a 
                href="https://humantraffickinghotline.org/" 
                target="_blank" 
                rel="noopener noreferrer"
                className="flex items-center gap-2"
              >
                <ExternalLink className="w-4 h-4" />
                Trafficking Hotline
              </a>
            </Button>
            <Link href="/safety-guidelines">
              <Button variant="ghost" size="sm">
                View Full Guidelines
              </Button>
            </Link>
          </div>
        </div>
        
        <Button
          variant="ghost"
          size="icon"
          className="flex-shrink-0"
          onClick={() => setDismissed(true)}
        >
          <X className="w-5 h-5" />
        </Button>
      </div>
    </div>
  );
}

/**
 * Warning banner shown when viewing personal connection category
 */
export function PersonalConnectionWarning() {
  return (
    <div className="bg-secondary/10 border border-secondary/30 rounded-lg p-4 mb-6">
      <div className="flex items-start gap-3">
        <Shield className="w-5 h-5 text-secondary flex-shrink-0 mt-0.5" />
        <div>
          <h4 className="font-semibold text-secondary">Personal Connections Category</h4>
          <p className="text-sm text-muted-foreground mt-1">
            This category requires wallet verification for posting. All listings are reviewed by our 
            moderation team. Listo has zero tolerance for illegal activity and works with law 
            enforcement to investigate suspicious content.
          </p>
          <p className="text-sm font-medium mt-2">
            If you see anything suspicious, please use the Report button immediately.
          </p>
        </div>
      </div>
    </div>
  );
}
